/*
 * FILE: unified_ui/src/lib/threadDividers.ts
 * PURPOSE: Pure thread-boundary divider logic for the chat transcript.
 *          Dividers are local pseudo-messages (never persisted, never sent to
 *          the agent) that visually mark transitions between chat threads.
 * OWNS: threadDividerLabel, withThreadDividers.
 * EXPORTS: ThreadMessage, threadDividerLabel, withThreadDividers.
 * DOCS: .agents/reports/plan_chat-threads_2026-08-11.md
 */

import type { ChatAction } from './commands'

/** Structural message shape — compatible with stores/chat.ts ChatMessage. */
export type ThreadMessage = {
  id: string
  role: 'user' | 'assistant' | 'system' | 'agent' | 'divider'
  text: string
  ts: string
  actions?: ChatAction[]
  live?: boolean
  threadId?: number | null
  /** Thread kind of the boundary the divider opens (set on divider messages);
   *  mirrors the ChatMessage field so the message surface can badge static copies. */
  threadKind?: 'agent' | 'static' | null
}

/** Label for a thread boundary divider (rendered as "— <label> —").
 *  Static threads (adapter visibility copies) vs agent threads. */
export function threadDividerLabel(kind: 'agent' | 'static'): string {
  return kind === 'static' ? 'Из переписки с ботом' : 'Новый чат'
}

/**
 * Insert thread-boundary dividers between adjacent messages whose threadId
 * differs. Pure function — operates on a chronological list; used by both
 * loadHistory and loadMoreHistory so pagination seams get dividers too.
 *
 * Rules:
 * - Transition INTO a labeled thread (id !== null) → divider with that
 *   thread's label ("Новый чат" for agent, "Из переписки с ботом" for static).
 * - Transition INTO the legacy NULL thread → no divider (it is the chat's
 *   original opening; the greeting already marks the start).
 * - Unknown thread ids default to the agent label.
 */
export function withThreadDividers(
  list: ThreadMessage[],
  threadKinds?: Map<number, 'agent' | 'static'>,
): ThreadMessage[] {
  const out: ThreadMessage[] = []
  let lastThreadId: number | null | undefined = undefined
  for (const m of list) {
    const tid = m.threadId ?? null
    if (lastThreadId !== undefined && tid !== lastThreadId) {
      if (tid !== null) {
        const kind = threadKinds?.get(tid) ?? 'agent'
        out.push({
          id: `m_div_${m.id}`,
          role: 'divider',
          text: threadDividerLabel(kind),
          ts: m.ts,
          threadId: tid,
          threadKind: kind,
        })
      }
    }
    out.push(m)
    lastThreadId = tid
  }
  return out
}
